import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import adrActions from "../../_actions/adrActions";
import Badge from "../_common/Badge";
import BadgeContainer from "../_common/BadgeContainer";
import { Collapse } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./styles/adrTentativeAgreement.css";
import AlertModal from "../_common/AlertModal";

function AdrTentativeAgreementFileUploadV3(props) {
  const {
    attachments,
    add,
    update,
    fileUploadContext,
    addTempFilePaths,
    markTaDirty,
    isLocked,
    adrIsReadOnly,
  } = props;

  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showSection, _toggleSection] = useState(false);
  const toggleSection = () => _toggleSection(!showSection);

  const dispatch = useDispatch();

  // Memoize attachments NOT marked for deletion
  const _attachments = useMemo(
    () => (attachments || []).filter((a) => !a.isDeleted),
    [attachments]
  );

  const [showAlert_wrongType, setShowAlert_wrongType] = useState(false);
  const toggleAlert_wrongType = () => setShowAlert_wrongType(!showAlert_wrongType);

  const handleChange_fileInput = (e) => {
    const newFiles = Array.from(e.target.files);
    const combinedFilesToUpload = [...filesToUpload];
    let alert = false;

    newFiles.forEach((file) => {
      if ([ ".pdf", ".jpg", ".jpeg" ].includes(file.name.substring(file.name.lastIndexOf(".")).toLowerCase())) {
        combinedFilesToUpload.push(file);
      } else if (!alert) {
        alert = true;
        e.target.value = null;
        toggleAlert_wrongType();
      }
    });
    setFilesToUpload(combinedFilesToUpload);
  };

  const handleChange = (fileRowIndex) => (e) => {
    const fieldToUpdate = { [e.target.name]: e.target.value };
    update(fileRowIndex, fieldToUpdate);
  };

  const handleClick_image = (attachment) => (e) => {
    e.preventDefault();
    dispatch(adrActions.openFile(attachment.filePath));
  };

  const handleClick_delete = (fileRowIndex) => (e) => {
    e.preventDefault();
    const attToDel = _attachments[fileRowIndex];
    if (attToDel) {
      const fieldToUpdate = {
        isDeleted: !attToDel.isDeleted,
      };
      update(fileRowIndex, fieldToUpdate);
    }
  };

  const handleClick_removeFileToUpload = (fileIndex) => {
    // Must shallow-copy
    const _filesToUpload = [...filesToUpload];
    _filesToUpload.splice(fileIndex, 1);
    setFilesToUpload(_filesToUpload);
  };

  const handleClick_uploadFile = async (e) => {
    e.preventDefault();
    // Initiate upload
    setIsUploading(true);
    const formData = new FormData();

    filesToUpload.forEach((file, i) => formData.append(i, file));
    for (const key in fileUploadContext) {
      formData.append(key, fileUploadContext[key]);
    }

    try {
      const uploadedFiles = await dispatch(adrActions.uploadAttachmentV2(formData));

      // Uploaded file paths are returned by the server. If user
      // quits adr without saving (by closing the adr tab), the added files are deleted
      // (hence the need for "temp file paths").
      // const uploadedFiles = {
      //   /** @type {string[]} */
      //   originalFileNames: res.originalFileNames || [],
      //   /** @type {string[]} */
      //   filePaths: res.filePaths || [],
      //   /** @type {string[]} */
      //   imageThumbnailFilePaths: res.imageThumbnailFilePaths || [],
      //   /** @type {string[]} */
      //   thumbnailBase64s: res.thumbnailBase64s || [],
      //   /** @type {Date} */
      //   createdOn: res.createdOn,
      // };

      /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementAttachmentDto[]} */
      const attachmentsToAdd = (uploadedFiles || []).map((f) => ({
        tentativeAgreementAttachmentId: 0,
        tentativeAgreementId: 0, // Will be fixed up by backend
        originalFilename: f.originalFilename,
        filePath: f.fileRelPath,
        imageThumbnailFilePath: f.imageThumbnailFileRelPath,
        thumbnailBase64: f.thumbnailBase64,
        description: "",
        createdOn: f.createdOn,
        isCustomTa: false,
        isDeleted: false,
      }));

      const tempFilePaths = (uploadedFiles || [])
        .filter((f) => f)
        .map((f) => f.fileRelPath);
      const tempThumbnailFilePaths = (uploadedFiles || [])
        .filter((f) => f)
        .map((f) => f.imageThumbnailFileRelPath);

      addTempFilePaths([...tempFilePaths, ...tempThumbnailFilePaths]);
      add(attachmentsToAdd);

      // Clear the file "holding area" once upload is complete
      setFilesToUpload([]);
      setIsUploading(false);
      markTaDirty();
    } catch (err) {
      setIsUploading(false);
    }
  };

  const getThumbnail = (attachment) => {
    const fileExt = attachment.filePath.split(".").pop().toLowerCase();
    // Get thumbnail if path exists, if not return file icon

    if (attachment.imageThumbnailFilePath)
      return (
        <img
          src={attachment.thumbnailBase64}
          alt="(Cannot display thumbnail)"
          height="90"
          width="auto"
          className="pointer-cursor"
          onClick={handleClick_image(attachment)}
        />
      );
    else if (fileExt === "pdf")
      return (
        <FontAwesomeIcon
          icon={["far", "file-pdf"]}
          className="fa-fw fa-4x pointer-cursor"
          onClick={handleClick_image(attachment)}
        />
      );
    else if (fileExt === "xlsx")
      return (
        <FontAwesomeIcon
          icon={["far", "file-excel"]}
          className="fa-fw fa-4x pointer-cursor"
          onClick={handleClick_image(attachment)}
        />
      );
    else if (fileExt === "docx")
      return (
        <FontAwesomeIcon
          icon={["far", "file-word"]}
          className="fa-fw fa-4x pointer-cursor"
          onClick={handleClick_image(attachment)}
        />
      );
    else
      return (
        <FontAwesomeIcon
          icon={["far", "file"]}
          className="fa-fw fa-4x pointer-cursor"
          onClick={handleClick_image(attachment)}
        />
      );
  };

  return (
    <React.Fragment>
      <div className="card mb-2 ">
        <div className="card-body p-0 m-2">
          <h5 className="text-muted pointer-cursor mb-0" onClick={toggleSection}>
            <FontAwesomeIcon
              icon={showSection ? "caret-down" : "caret-right"}
              className="fa-fw"
            />
            Attachments
            <span className="badge badge-pill badge-secondary my-auto ml-2">
              {_attachments.length}
            </span>
          </h5>
          <Collapse isOpen={showSection}>
            {(!adrIsReadOnly || !isLocked) && (
              <React.Fragment>
                <div className="card bg-lightgray mt-2 mb-2">
                  <div className="card-body p-0 m-2">
                    <div className="form-row form-group mb-0">
                      <div className="col-sm-8">
                        <div className="custom-file">
                          <input
                            type="file"
                            id="taFile"
                            name="taFile"
                            className="custom-file-input"
                            onChange={handleChange_fileInput}
                            accept={".pdf, .jpg, .jpeg"}
                            multiple
                            disabled={isLocked}
                          />
                          <label htmlFor="file" className="custom-file-label">
                            Choose files...
                          </label>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <button
                          className="btn btn-primary mr-2"
                          name="submit_uploadFile"
                          onClick={handleClick_uploadFile}
                          disabled={filesToUpload.length === 0 || isUploading}
                        >
                          <FontAwesomeIcon icon="upload" className="fa-fw mr-1" />
                          Upload
                        </button>
                        {isUploading && (
                          <span>
                            <FontAwesomeIcon
                              icon="spinner"
                              className="loading-spinner mr-2"
                            />
                            <small>Uploading...</small>
                          </span>
                        )}
                      </div>
                    </div>
                    {filesToUpload.length > 0 && (
                      <BadgeContainer>
                        {filesToUpload.map((f, index) => (
                          <Badge
                            className="mr-1"
                            key={`fileLabelIndex_${index}`}
                            itemId={index}
                            clickItem={() => {}}
                            deleteItem={handleClick_removeFileToUpload}
                            label={f.name}
                            showDelete={true}
                          />
                        ))}
                      </BadgeContainer>
                    )}
                  </div>
                </div>
                <hr className="my-2" />
                <h6>
                  <FontAwesomeIcon icon="folder-open" className="fa-fw mr-2" />
                  Uploaded Files
                </h6>
              </React.Fragment>
            )}
            <div className="pl-0 border ta-attach__table mb-2">
              <div className="d-flex flex-row py-1 bg-lightgray ta-attach__table-header">
                <div className="px-1 ta-attach__table-col--file">
                  <b>File</b>
                </div>
                <div className="px-1 ta-attach__table-col--desc">
                  <b>Description/Notes</b>
                </div>
                <div className="px-1 ta-attach__table-col--del"></div>
              </div>
              {_attachments && _attachments.length > 0 ? (
                _attachments.map((attachment, fileRowIndex) => (
                  <div
                    key={`attachRowKey_${fileRowIndex}`}
                    className="d-flex flex-row py-1 pl-1 ta-attach__table-row"
                  >
                    <div className="px-1 ta-attach__table-col--file">
                      <div className="d-flex flex-column">
                        <div>{getThumbnail(attachment)}</div>
                        <div>
                          <span className="text-small">
                            {attachment.originalFilename}
                          </span>
                        </div>
                      </div>
                    </div>
                    {!attachment.isCustomTa ? (
                      <React.Fragment>
                        <div className="px-1 ta-attach__table-col--desc">
                          <textarea
                            name="description"
                            id={`taa_desc_${fileRowIndex}`}
                            className="form-control w-100"
                            rows="3"
                            value={attachment.description}
                            onChange={handleChange(fileRowIndex)}
                            disabled={isLocked}
                          />
                        </div>
                        <div className="px-1 ta-attach__table-col--del flex-grow-1 text-right">
                          <button
                            type="button"
                            name="delAttach"
                            id={`taa_del_${fileRowIndex}`}
                            className="btn btn-danger px-2"
                            onClick={handleClick_delete(fileRowIndex)}
                            disabled={isLocked}
                          >
                            <FontAwesomeIcon icon="times" className="fa-fw" />
                          </button>
                        </div>
                      </React.Fragment>
                    ) : (
                      <div className="px-1 ta-attach__table-col--desc">
                        <div>
                          <span>
                            <em>Custom TA form</em>
                          </span>
                          <br />
                          <span className="text-small">
                            Upload new or delete from "Form Type" above.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="d-flex flex-row py-1 pl-1 ta-attach__table-row">None</div>
              )}
            </div>
          </Collapse>
        </div>
      </div>
      <AlertModal
        alertKey={"wrongType"}
        alertTitle={"Invalid File Type"}
        alertTitleClass="bg-warning"
        alertTitleIcon="exclamation-triangle"
        alertText="Only .pdf, .jpg, and .jpeg files are supported."
        acceptCaption={"OK"}
        acceptClass={"secondary"}
        provideReject={false}
        handleAlertAction={toggleAlert_wrongType}
        showAlertModal={showAlert_wrongType}
        toggleAlertModal={toggleAlert_wrongType}
      />
    </React.Fragment>
  );
}

export default AdrTentativeAgreementFileUploadV3;
